import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    useTheme,
    Button,
    Stack,
    Skeleton,
} from '@mui/material';
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import axiosInstance from '../../../axios/axios';
import { END_POINTS } from '../../../shared';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';

type TrendGraphPoint = {
    timestamp?: string;
    posture_score?: number;
    num_changes?: number;
    new_event?: boolean;
};

// API can provide trend data either as a raw array, wrapped in a `data` field, or in the new format with `all`, `total_changes`, and `total_posture_score`
type TrendGraphPayload =
    | TrendGraphPoint[]
    | { data?: TrendGraphPoint[] }
    | { all?: TrendGraphPoint[]; total_changes?: number; total_posture_score?: number }
    | undefined;

type TrendsData = {
    hours: string[];
    timestamps: string[]; // Full timestamps for tooltip display
    postureScores: number[];
    numberOfChanges: number[];
    newEvents: boolean[]; // Track which points have new events
    latestPostureScore: number;
    latestNumChanges: number;
    totalPostureScore: number;
    totalChanges: number;
};

type TrendsCardProps = {
    height?: number;
    compact?: boolean;
    trendGraphData?: any;
    deviceId?: string;
};

const roundValue = (value?: number): number => {
    if (typeof value !== "number" || isNaN(value)) return 0;
    return Math.round(value);
};

const TrendsCard: React.FC<TrendsCardProps> = ({ height, compact = false, trendGraphData, deviceId }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const [isExpanded, setIsExpanded] = useState(true);
    const [timeRange, setTimeRange] = useState('30days');
    const [fetchedTrendData, setFetchedTrendData] = useState<any>(null);
    const [isChartLoading, setIsChartLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [dateRangeError, setDateRangeError] = useState<string>('');
    const [selectOpen, setSelectOpen] = useState(false);
    const [customDays, setCustomDays] = useState<number | null>(null);
    const [appliedFromDate, setAppliedFromDate] = useState<Dayjs | null>(null);
    const [appliedToDate, setAppliedToDate] = useState<Dayjs | null>(null);

    const normaliseTrendGraphData = (
        payload?: TrendGraphPayload,
        currentTimeRange?: string
    ): TrendsData => {

        let dataArray: TrendGraphPoint[] = [];
        let totalPostureScore = 0;
        let totalChanges = 0;

        if (Array.isArray(payload)) {
            dataArray = payload;
        } else if (payload && typeof payload === 'object') {
            if ('all' in payload && Array.isArray(payload.all)) {
                dataArray = payload.all;
                totalPostureScore = roundValue(payload.total_posture_score);
                totalChanges = roundValue(payload.total_changes);
            } else if ('data' in payload && Array.isArray(payload.data)) {
                dataArray = payload.data;
            }
        }

        if (dataArray.length === 0) {
            return {
                hours: [],
                timestamps: [],
                postureScores: [],
                numberOfChanges: [],
                newEvents: [],
                latestPostureScore: 0,
                latestNumChanges: 0,
                totalPostureScore: 0,
                totalChanges: 0,
            };
        }

        const timestampFormats = ['DD MMM YYYY HH:mm:ss', moment.ISO_8601];

        const sortedData = [...dataArray].sort((a, b) => {
            const first = moment(a?.timestamp, timestampFormats, true);
            const second = moment(b?.timestamp, timestampFormats, true);
            const firstValue = first.isValid() ? first.valueOf() : 0;
            const secondValue = second.isValid() ? second.valueOf() : 0;
            return firstValue - secondValue;
        });

        // Format x-axis labels based on time range
        // Calculate the actual number of days for the range
        let daysInRange = 30; // Default
        if (currentTimeRange === '7days') {
            daysInRange = 7;
        } else if (currentTimeRange === '30days') {
            daysInRange = 30;
        } else if (currentTimeRange === '90days') {
            daysInRange = 90;
        } else if (currentTimeRange === '180days') {
            daysInRange = 180;
        } else if (currentTimeRange === 'custom' && sortedData.length >= 2) {
            // For custom range, calculate days from the actual data
            const firstDate = moment(sortedData[0]?.timestamp, timestampFormats, true);
            const lastDate = moment(sortedData[sortedData.length - 1]?.timestamp, timestampFormats, true);
            if (firstDate.isValid() && lastDate.isValid()) {
                daysInRange = lastDate.diff(firstDate, 'days') + 1;
            }
        }

        let hours: string[];

        if (daysInRange <= 15) {
            // For ranges ≤ 15 days: show date and month (e.g., "12 Dec", "13 Dec", etc.)
            hours = sortedData.map((point) => {
                const parsed = moment(point.timestamp, timestampFormats, true);
                return parsed.isValid() ? parsed.format("DD MMM") : point.timestamp ?? '—';
            });
        } else if (daysInRange <= 31) {
            // For 1 month: show month-year labels without duplicates
            const seenMonths = new Set<string>();
            hours = sortedData.map((point) => {
                const parsed = moment(point.timestamp, timestampFormats, true);
                if (!parsed.isValid()) return point.timestamp ?? '—';

                // Use YYYY-MM as the unique key to ensure each month appears only once
                const monthKey = parsed.format('YYYY-MM'); // Year and month
                const dateLabel = parsed.format("MMM 'YY");

                // Show first occurrence of each month
                if (seenMonths.has(monthKey)) {
                    return ''; // Return empty string for same month
                }
                seenMonths.add(monthKey);
                return dateLabel;
            });
        } else {
            // For longer periods (90 days, 180 days, custom > 31 days): show all unique months
            const seenMonths = new Set<string>();

            hours = sortedData.map((point) => {
                const parsed = moment(point.timestamp, timestampFormats, true);
                const monthLabel = parsed.isValid() ? parsed.format("MMM 'YY") : point.timestamp ?? '—';

                // Only show label for first occurrence of each month
                if (seenMonths.has(monthLabel)) {
                    return ''; // Return empty string for duplicate months
                }
                seenMonths.add(monthLabel);
                return monthLabel;
            });
        }

        const timestamps = sortedData.map((point) => {
            const parsed = moment(point.timestamp, timestampFormats, true);
            // Store full formatted timestamp for tooltip (e.g., "DD MMM YYYY HH:mm:ss")
            return parsed.isValid() ? parsed.format('DD MMM YYYY HH:mm:ss') : point.timestamp ?? '—';
        });

        // Show all posture scores (line is always visible like in Cyber Resilience)
        // Blue dots appear only where new_event is true (via discrete markers)
        const postureScores = sortedData.map((point) =>
            typeof point.posture_score === 'number' ? point.posture_score : 0
        );
        const numberOfChanges = sortedData.map((point) =>
            typeof point.num_changes === 'number' ? point.num_changes : 0
        );
        // Extract new_event flags for each data point (for event-based markers like Cyber Resilience)
        const newEvents = sortedData.map((point) =>
            point.new_event === true
        );

        // If total values weren't provided in the payload, calculate from latest point (backward compatibility)
        if (totalPostureScore === 0 && totalChanges === 0) {
            const latestPoint = sortedData[sortedData.length - 1];
            totalPostureScore = typeof latestPoint?.posture_score === 'number'
                ? latestPoint.posture_score
                : postureScores[postureScores.length - 1] ?? 0;
            totalChanges = typeof latestPoint?.num_changes === 'number'
                ? latestPoint.num_changes
                : numberOfChanges[numberOfChanges.length - 1] ?? 0;
        }

        // Calculate latest values for display (matching EventsTrendsCard)
        const latestPostureScore = typeof totalPostureScore === 'number' && totalPostureScore > 0
            ? totalPostureScore
            : postureScores[postureScores.length - 1] ?? 0;
        const latestNumChanges = typeof totalChanges === 'number' && totalChanges > 0
            ? totalChanges
            : numberOfChanges[numberOfChanges.length - 1] ?? 0;

        return {
            hours,
            timestamps,
            postureScores,
            numberOfChanges,
            newEvents,
            latestPostureScore,
            latestNumChanges,
            totalPostureScore,
            totalChanges,
        };
    };

    // Use fetched data if available, otherwise use prop data (for backward compatibility)
    const activeTrendData = useMemo(() => {
        return fetchedTrendData !== null ? fetchedTrendData : trendGraphData;
    }, [fetchedTrendData, trendGraphData]);

    const trendsData = useMemo(
        () => normaliseTrendGraphData(activeTrendData, timeRange),
        [activeTrendData, timeRange]
    );

    // Map timeRange to days
    const getDaysFromTimeRange = (range: string): number => {
        switch (range) {
            case '7days':
                return 7;
            case '30days':
                return 30;
            case '90days':
                return 90;
            case '180days':
                return 180;
            case 'custom':
                return customDays || 7;
            default:
                return 7;
        }
    };

    // Get display text for timeRange
    const getTimeRangeDisplayText = (range: string): string => {
        if (range === 'custom' && appliedFromDate && appliedToDate) {
            const fromDateStr = appliedFromDate.format('DD MMM YYYY');
            const toDateStr = appliedToDate.format('DD MMM YYYY');
            return `${fromDateStr} - ${toDateStr}`;
        }
        switch (range) {
            case '7days':
                return 'Last 7 Days';
            case '30days':
                return 'Last 1 Month';
            case '90days':
                return 'Last 3 Month';
            case '180days':
                return 'Last 6 Month';
            default:
                return 'Last 7 Days';
        }
    };

    const fetchTrendsData = useCallback(async (days: number | null, customStartDate?: string, customEndDate?: string) => {
        if (!deviceId) return;
        setIsChartLoading(true);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        let startDate: string;
        let endDate: string;

        if (customStartDate && customEndDate) {
            startDate = customStartDate;
            endDate = customEndDate;
        } else {
            startDate = moment()
                .subtract(days, "days")
                .format("YYYY-MM-DD");
            endDate = moment().format("YYYY-MM-DD");
        }

        try {
            const response = await axiosInstance.get(
                `${END_POINTS.ASSET_CONFIG_DIFF_TREND_GRAPH}/${deviceId}/trends?start_date=${startDate}&end_date=${endDate}&timezone=${timeZone}`,

            );
            if (response.data.status === 200 || response.status === 200) {
                const graphData = response?.data?.data || null;
                setFetchedTrendData(graphData);
            }
        } catch (error: any) {
            toast.error(error.message);
            setFetchedTrendData(null);
        } finally {
            setIsChartLoading(false);
        }
    }, [deviceId]);

    // Validate date range (minimum 7 days)
    const validateDateRange = (from: Dayjs | null, to: Dayjs | null): string => {
        if (!from || !to) {
            return 'Please select both from and to dates';
        }
        if (from.isAfter(to)) {
            return 'From date cannot be after To date';
        }
        const daysDiff = to.diff(from, 'day');
        if (daysDiff < 7) {
            return 'You can select a minimum period of 7 days';
        }
        return '';
    };

    // Handle apply button click for custom date range
    const handleApplyCustomDateRange = () => {
        const error = validateDateRange(fromDate, toDate);
        if (error) {
            setDateRangeError(error);
            return;
        }
        setDateRangeError('');
        if (deviceId && fromDate && toDate) {
            const startDate = fromDate.format('YYYY-MM-DD');
            const endDate = toDate.format('YYYY-MM-DD');
            // Calculate the number of days
            const days = toDate.diff(fromDate, 'day');
            setCustomDays(days);
            // Store the applied dates for display
            setAppliedFromDate(fromDate);
            setAppliedToDate(toDate);
            setTimeRange('custom');
            fetchTrendsData(null, startDate, endDate);
            setSelectOpen(false); // Close the Select dropdown
        }
    };

    // Reset state and fetch data when deviceId changes
    useEffect(() => {
        if (!deviceId) {
            setFetchedTrendData(null);
            setIsChartLoading(false);
            return;
        }

        // Set loading state FIRST to prevent "No data available" flash
        setIsChartLoading(true);

        // Reset state when device changes
        setFetchedTrendData(null);
        setFromDate(null);
        setToDate(null);
        setDateRangeError('');
        setTimeRange('30days');
        setSelectOpen(false);
        setCustomDays(null);
        setAppliedFromDate(null);
        setAppliedToDate(null);

        // Fetch initial data with default 30 days range (1 month)
        const days = getDaysFromTimeRange('30days');
        fetchTrendsData(days);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    // Fetch data when timeRange changes (only for non-custom ranges)
    // Skip if this is the initial load (timeRange already set to '30days' above)
    const initialLoadRef = React.useRef(true);
    useEffect(() => {
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        if (deviceId && timeRange !== 'custom') {
            const days = getDaysFromTimeRange(timeRange);
            fetchTrendsData(days);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange]);

    // Calculate dynamic max values for y-axes with padding
    const maxPostureScore = useMemo(() => {
        const max = Math.max(...trendsData.postureScores, 0);
        // Round up to nearest integer and add 1 for padding
        return max > 0 ? Math.ceil(max) + 1 : 100;
    }, [trendsData.postureScores]);

    const maxNumChanges = useMemo(() => {
        const max = Math.max(...trendsData.numberOfChanges, 0);
        // Round up to nearest integer and add 1 for padding
        return max > 0 ? Math.ceil(max) + 1 : 100;
    }, [trendsData.numberOfChanges]);

    const chartOptions: ApexOptions = useMemo(
        () => {
            // Generate discrete markers for data points where new_event is true
            const discreteMarkers: Array<{
                seriesIndex: number;
                dataPointIndex: number;
                fillColor: string;
                strokeColor: string;
                size: number;
            }> = [];

            // Show blue dots on posture score line only when new_event is true
            trendsData.newEvents.forEach((isEvent, index) => {
                if (isEvent) {
                    // Add blue marker for Posture Score line (series 0) when new_event is true
                    discreteMarkers.push({
                        seriesIndex: 0,
                        dataPointIndex: index,
                        fillColor: '#3A2ADA', // Blue color to match posture score line
                        strokeColor: '#fff',
                        size: 6,
                    });
                }
            });

            // Calculate intervals based on data span and time range
            let monthlyIntervals: number[] = [];
            let dataSpanMonths = 0;

            // Calculate the expected date range based on timeRange selection
            let rangeStartDate: dayjs.Dayjs;
            let rangeEndDate = dayjs(); // Today
            let rangeSpanMonths = 0;

            if (timeRange === 'custom' && appliedFromDate && appliedToDate) {
                rangeStartDate = appliedFromDate.startOf('day');
                rangeEndDate = appliedToDate.startOf('day');
            } else {
                const days = getDaysFromTimeRange(timeRange);
                rangeStartDate = dayjs().subtract(days, 'days').startOf('day');
            }

            // Calculate the time range span in months
            rangeSpanMonths = rangeEndDate.diff(rangeStartDate, 'month', true);

            if (trendsData.timestamps.length > 0) {
                const firstTimestamp = dayjs(trendsData.timestamps[0]).valueOf();
                const lastTimestamp = dayjs(trendsData.timestamps[trendsData.timestamps.length - 1]).valueOf();
                const firstDate = dayjs(firstTimestamp);
                const lastDate = dayjs(lastTimestamp);

                // Calculate data span in months
                dataSpanMonths = lastDate.diff(firstDate, 'month', true);

                // If the selected time range spans more than 2 months, generate monthly intervals from the range start
                if (rangeSpanMonths > 2) {
                    // Generate monthly intervals from the start of the selected range to the end
                    let currentDate = rangeStartDate.startOf('month');
                    while (!currentDate.isAfter(rangeEndDate, 'month')) {
                        monthlyIntervals.push(currentDate.valueOf());
                        currentDate = currentDate.add(1, 'month');
                    }
                } else if (timeRange === '30days') {
                    // For 1 month view, generate monthly intervals to show month labels (e.g., Dec '25, Jan '26)
                    let currentDate = firstDate.startOf('month');
                    while (!currentDate.isAfter(lastDate, 'month')) {
                        monthlyIntervals.push(currentDate.valueOf());
                        currentDate = currentDate.add(1, 'month');
                    }
                }
            } else if (rangeSpanMonths > 2) {
                // Even if no data, generate monthly intervals for the selected range
                let currentDate = rangeStartDate.startOf('month');
                while (!currentDate.isAfter(rangeEndDate, 'month')) {
                    monthlyIntervals.push(currentDate.valueOf());
                    currentDate = currentDate.add(1, 'month');
                }
            }

            return {
                chart: {
                    height: height ?? (compact ? 10 : 40),
                    zoom: { enabled: false },
                    toolbar: { show: false },
                    parentHeightOffset: 0,
                    offsetX: 0,
                    offsetY: 0,
                },
                stroke: {
                    curve: 'smooth',
                    width: 1,
                },
                colors: ['#3A2ADA', '#B8A3FF'],
                dataLabels: {
                    enabled: false,
                },
                markers: {
                    size: 0, // Default no markers
                    discrete: discreteMarkers, // Only show blue markers for new_event points
                    hover: {
                        sizeOffset: 2,
                    },
                },
                xaxis: {
                    categories: trendsData.hours,
                    title: {
                        text: 'Time Stamp',
                        style: {
                            fontSize: '12px',
                            fontWeight: 700,
                            color: isDarkMode ? '#FFFFFF' : 'black',
                        },
                    },
                    labels: {
                        style: {
                            colors: isDarkMode ? '#FFFFFF' : 'black',
                            fontSize: '11px',
                        },
                        rotate: -45,
                        rotateAlways: true,
                        hideOverlappingLabels: false,
                        showDuplicates: false,
                        offsetX: 0,
                        offsetY: 0,
                        formatter: (value: string, timestamp?: number) => {
                            // For 30 days view, just return the category value directly (already processed in hours array)
                            if (timeRange === '30days') {
                                return value;
                            }

                            if (!timestamp) return value;

                            // If the selected time range spans more than 2 months, show monthly intervals only
                            if (rangeSpanMonths > 2 && monthlyIntervals.length > 0) {
                                const isMonthlyInterval = monthlyIntervals.some(monthTimestamp => {
                                    const diff = Math.abs(timestamp - monthTimestamp);
                                    const diffDays = diff / (1000 * 60 * 60 * 24);
                                    // Allow within 5 days of the first day of month
                                    return diffDays <= 5;
                                });

                                if (isMonthlyInterval) {
                                    return dayjs(timestamp).format("MMM 'YY");
                                }
                                return "";
                            }

                            // For other ranges, use default formatting
                            return dayjs(timestamp).format("MMM 'YY");
                        },
                        datetimeFormatter: {
                            year: "yyyy",
                            month: "MMM 'YY",
                            day: "DD MMM",
                            hour: "HH:mm",
                        },
                    },
                    tickAmount: timeRange === '30days' ? undefined : undefined,
                    axisBorder: {
                        color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                    },
                    axisTicks: {
                        color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                    },
                },
                yaxis: [
                    {
                        title: {
                            text: 'Posture score',
                            style: {
                                fontSize: '12px',
                                fontWeight: 700,
                                color: isDarkMode ? '#FFFFFF' : 'black',
                            },
                        },
                        labels: {
                            style: {
                                colors: isDarkMode ? '#FFFFFF' : 'black',
                                fontSize: '12px',
                                fontWeight: 500,
                            },
                            formatter: (val: number) => `${Math.round(val)}`,
                        },
                        min: 0,
                        max: maxPostureScore,
                        tickAmount: Math.min(5, maxPostureScore),
                        forceNiceScale: false,
                    },
                    {
                        opposite: true,
                        title: {
                            text: 'No. of Changes',
                            style: {
                                fontSize: '12px',
                                fontWeight: 700,
                                color: isDarkMode ? '#FFFFFF' : 'black',
                            },
                        },
                        labels: {
                            style: {
                                colors: isDarkMode ? '#FFFFFF' : 'black',
                                fontSize: '12px',
                                fontWeight: 700,
                            },
                            formatter: (val: number) => `${Math.round(val)}`,
                        },
                        min: 0,
                        max: maxNumChanges,
                        tickAmount: Math.min(5, maxNumChanges),
                        forceNiceScale: false,
                    },
                ],
                tooltip: {
                    shared: true,
                    intersect: false,
                    theme: theme.palette.mode,
                    x: {
                        formatter: (val: any, opts?: any) => {
                            // Get the dataPointIndex from opts
                            const dataPointIndex = opts?.dataPointIndex;
                            if (dataPointIndex !== undefined && trendsData.timestamps[dataPointIndex]) {
                                // Return date only without time (DD MMM YYYY)
                                const fullTimestamp = trendsData.timestamps[dataPointIndex];
                                // Parse and format to remove time portion
                                const parsed = moment(fullTimestamp, 'DD MMM YYYY HH:mm:ss');
                                return parsed.isValid() ? parsed.format('DD MMM YYYY') : fullTimestamp;
                            }
                            return val;
                        },
                    },
                },
                legend: {
                    show: false,
                },
                grid: {
                    borderColor: isDarkMode ? '#FFFFFF' : '#e7e7e7',
                    padding: {
                        left: 5,
                        right: 5,
                        top: 0,
                        bottom: 0,
                    },
                },
                plotOptions: {
                    bar: {
                        columnWidth: '80%',
                    },
                },
            };
        },
        [trendsData.hours, trendsData.newEvents, theme, height, compact, maxPostureScore, maxNumChanges, isDarkMode]
    );

    const chartSeries = [
        {
            name: 'Posture Score',
            type: 'line',
            data: trendsData.postureScores,
        },
        {
            name: 'Number of changes',
            type: 'column',
            data: trendsData.numberOfChanges,
        },
    ];

    const hasNoTrendData = !isChartLoading && trendsData.hours.length === 0;

    return (
        <Accordion
            expanded={isExpanded}
            onChange={(_event, expanded) => setIsExpanded(expanded)}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                bgcolor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#ffffff',
                boxShadow: 'none',
                margin: 0,
                '&:before': {
                    display: 'none',
                },
                mb: 2,
            }}
            disableGutters
            elevation={0}
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon
                        sx={{
                            color: theme.palette.text.primary,
                            transition: 'transform 0.3s',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                }
                sx={{
                    flexDirection: 'row-reverse',
                    borderBottom: isExpanded ? `1px solid ${theme.palette.divider}` : 'none',
                    minHeight: '56px',
                    '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        alignItems: 'center',
                        width: '100%',
                    },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                            fontSize: '14px',
                            color: theme.palette.text.primary,
                        }}
                    >
                        Trends
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: '#3A2ADA',
                                    }}
                                />
                                <span style={{ fontSize: '13px', fontWeight: 500, color: theme.palette.text.secondary }}>
                                    Posture Score: {Math.ceil(trendsData.latestPostureScore)
                                        .toString()
                                        .padStart(2, '0')}
                                </span>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: '#8787AE',
                                    }}
                                />
                                <span style={{ fontSize: '13px', fontWeight: 500, color: theme.palette.text.secondary }}>
                                    No. of Changes: {Math.ceil(trendsData.latestNumChanges)
                                        .toString()
                                        .padStart(2, '0')}
                                </span>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }} onClick={(e) => e.stopPropagation()}>
                                <Select
                                    value={timeRange}
                                    open={selectOpen}
                                    onClose={() => setSelectOpen(false)}
                                    onOpen={() => setSelectOpen(true)}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        const newValue = e.target.value;
                                        // Always reset dates when switching between menu items
                                        setFromDate(null);
                                        setToDate(null);
                                        setDateRangeError('');
                                        setTimeRange(newValue);
                                        setCustomDays(null); // Reset custom days when selecting predefined range
                                        setAppliedFromDate(null); // Reset applied dates when selecting predefined range
                                        setAppliedToDate(null); // Reset applied dates when selecting predefined range
                                        setSelectOpen(false); // Close the Select dropdown
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    renderValue={(value) => getTimeRangeDisplayText(value)}
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 800,
                                        height: '32px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.divider,
                                        },
                                    }}
                                >
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Stack spacing={2} padding={1}>
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                <DatePicker
                                                    label="From"
                                                    value={fromDate}
                                                    onChange={(newValue) => {
                                                        setFromDate(newValue);
                                                        if (newValue && toDate) {
                                                            const error = validateDateRange(newValue, toDate);
                                                            setDateRangeError(error);
                                                        } else {
                                                            setDateRangeError('');
                                                        }
                                                    }}
                                                    maxDate={dayjs()}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '12px',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                <DatePicker
                                                    label="To"
                                                    value={toDate}
                                                    onChange={(newValue) => {
                                                        setToDate(newValue);
                                                        if (newValue) {
                                                            const error = validateDateRange(fromDate, newValue);
                                                            setDateRangeError(error);
                                                        } else {
                                                            setDateRangeError('');
                                                        }
                                                    }}
                                                    maxDate={dayjs()}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '12px',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleApplyCustomDateRange}
                                                    disabled={!fromDate || !toDate || !!dateRangeError}
                                                    sx={{
                                                        height: '40px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Apply
                                                </Button>
                                            </Box>
                                            {dateRangeError ? (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: theme.palette.error.main,
                                                        fontSize: '12px',
                                                        fontStyle: "italic"
                                                    }}
                                                >
                                                    {dateRangeError}
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <InfoIcon sx={{
                                                        fontSize: 18,
                                                        color: theme.palette.mode === "dark" ? "white" : theme.palette.primary.main
                                                    }} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            fontStyle: "italic"
                                                        }}
                                                    >
                                                        You can select a minimum period of 7 days
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </LocalizationProvider>
                                    <MenuItem value="7days">Last 7 Days</MenuItem>
                                    <MenuItem value="30days">Last 1 Month</MenuItem>
                                    <MenuItem value="90days">Last 3 Months</MenuItem>
                                    <MenuItem value="180days">Last 6 Months</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, py: 0 }}>
                <Box sx={{ px: 0, py: 0, position: 'relative' }}>
                    {isChartLoading ? (
                        <Box sx={{
                            px: 3,
                            py: 2,
                            height: height ?? (compact ? 100 : 140),
                            minHeight: height ?? (compact ? 100 : 140),
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}>
                            {/* Y-axis labels skeleton */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={80} height={16} />
                            </Box>
                            {/* Chart area skeleton with bars and line points */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'space-around',
                                flex: 1,
                                gap: 1,
                                position: 'relative',
                                pb: 3,
                            }}>
                                {Array.from({ length: 7 }).map((_, index) => {
                                    const barHeight = [45, 35, 55, 40, 50, 38, 42][index] || 40;
                                    return (
                                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1, position: 'relative' }}>
                                            {/* Bar skeleton */}
                                            <Skeleton
                                                variant="rectangular"
                                                width="100%"
                                                height={`${barHeight}%`}
                                                sx={{ borderRadius: '4px 4px 0 0', minHeight: 20 }}
                                            />
                                            {/* Line point skeleton */}
                                            <Skeleton
                                                variant="circular"
                                                width={8}
                                                height={8}
                                                sx={{
                                                    position: 'absolute',
                                                    top: `${100 - barHeight - 5}%`,
                                                }}
                                            />
                                            {/* X-axis label skeleton */}
                                            <Skeleton variant="text" width={40} height={12} sx={{ mt: 'auto' }} />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ) : hasNoTrendData ? (
                        <Box
                            sx={{
                                px: 3,
                                py: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                height: height ?? (compact ? 100 : 140),
                                minHeight: height ?? (compact ? 100 : 140),
                            }}
                        >
                            No data available
                        </Box>
                    ) : (
                        <ReactApexChart
                            options={chartOptions}
                            series={chartSeries as any}
                            type="line"
                            height={height ?? (compact ? 100 : 140)}
                        />
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default TrendsCard;
